import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { API_URI } from '../../constants/GlobalConstants';
import {
  Button,
  Snackbar,
  Alert,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  CircularProgress,
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import {
  Download,
  Upload,
  Eye,
  FileText,
  Grid3x3,
} from 'lucide-react';

const MatrixListing = () => {
  const [matrices, setMatrices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const fileInputRef = useRef(null);

  const userLogin = useSelector((state) => state.userSignIn);
  const { userInfo } = userLogin;

  useEffect(() => {
    const fetchMatrices = async () => {
      if (!userInfo?.token) {
        setError("Authorization required");
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API_URI}/api/matrices`, {
          headers: {
            Authorization: `Bearer ${userInfo.token}`,
          },
        });
        setMatrices(response.data.data);
        setLoading(false);
      } catch (err) {
        setError('Failed to load matrices');
        setLoading(false);
      }
    };

    fetchMatrices();
  }, [userInfo]);

  const handleExport = () => {
    const dataStr = JSON.stringify(matrices, null, 2);
    const blob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'matrices.json';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API_URI}/api/matrices/import`, formData, {
        headers: {
          Authorization: `Bearer ${userInfo.token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      setSnackbarMessage(`Import completed: ${response.data.results.length} matrices processed, ${response.data.errors.length} errors`);
      setSnackbarSeverity(response.data.errors.length > 0 ? 'warning' : 'success');
      setSnackbarOpen(true);

      const updatedResponse = await axios.get(`${API_URI}/api/matrices`, {
        headers: {
          Authorization: `Bearer ${userInfo.token}`,
        },
      });
      setMatrices(updatedResponse.data.data);
    } catch (err) {
      setSnackbarMessage('Failed to import matrices: ' + (err.response?.data?.message || err.message));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
    event.target.value = null;
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const tableHeaderStyle = {
    color: '#bdbdbd',
    fontSize: '0.8rem',
    textTransform: 'uppercase',
    fontWeight: 'bold',
    letterSpacing: '0.05em',
  };

  const tableCellStyle = {
    color: '#e0e0e0',
    borderColor: '#424242',
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: '#121212' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={40} sx={{ color: '#90caf9', mb: 2 }} />
          <Typography variant="h6" sx={{ color: '#e0e0e0' }}>
            Loading matrices...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4, bgcolor: '##1A2232', minHeight: '100vh' }}>
        <Alert severity="error" sx={{ bgcolor: '##1A2232', color: '#ffcdd2', border: '1px solid #c62828' }}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4, bgcolor: '#1A2232', minHeight: '100vh', color: '#e0e0e0' }}>
      <Card sx={{ bgcolor: '#1A2232', mb: 3, boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)' }}>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
            <Grid3x3 size={28} color="#90caf9" />
            <Typography variant="h4" component="h1" sx={{ color: '#ffffff', fontWeight: 600 }}>
              Matrix Management
            </Typography>
          </Stack>
          <Typography variant="body1" sx={{ color: '#b0b0b0' }}>
            Manage matrices for square footage calculations in blinds manufacturing.
          </Typography>
        </CardContent>
      </Card>

      <Card sx={{ bgcolor: '#1A2232', mb: 3, boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              startIcon={<Download size={18} />}
              onClick={handleExport}
              sx={{
                bgcolor: '#1976d2',
                color: '#ffffff',
                '&:hover': { bgcolor: '#1565c0' },
              }}
            >
              Export Matrices
            </Button>
            <Button
              variant="outlined"
              startIcon={<Upload size={18} />}
              onClick={() => fileInputRef.current.click()}
              sx={{
                borderColor: '#1976d2',
                color: '#90caf9',
                '&:hover': {
                  borderColor: '#1565c0',
                  bgcolor: 'rgba(25, 118, 210, 0.08)',
                },
              }}
            >
              Import Matrices
            </Button>
            <input
              type="file"
              accept=".json"
              style={{ display: 'none' }}
              ref={fileInputRef}
              onChange={handleImport}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: { sm: 2 } }}>
              <Typography variant="body2" sx={{ color: '#b0b0b0' }}>
                Total Matrices:
              </Typography>
              <Typography variant="body2" sx={{ color: '#ffffff', fontWeight: 'bold' }}>
                {matrices.length}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {matrices.length === 0 ? (
        <Card sx={{ bgcolor: '#1A2232', border: '1px solid #424242', boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)' }}>
          <CardContent>
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <FileText size={48} color="#757575" style={{ marginBottom: '16px' }} />
              <Typography variant="h6" sx={{ color: '#e0e0e0', mb: 1 }}>
                No Matrices Found
              </Typography>
              <Typography variant="body2" sx={{ color: '#b0b0b0' }}>
                Start by importing matrices or create new ones to manage your calculations.
              </Typography>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper} sx={{ bgcolor: '#1A2232', border: '1px solid #424242', boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)' }}>
          <Table sx={{ minWidth: 650 }} aria-label="matrix table">
            <TableHead sx={{ bgcolor: '#1A2232' }}>
              <TableRow>
                <TableCell sx={tableHeaderStyle}>#</TableCell>
                <TableCell sx={tableHeaderStyle}>Dependencies</TableCell>
                <TableCell sx={tableHeaderStyle}>Width Array</TableCell>
                <TableCell sx={tableHeaderStyle}>Height Array</TableCell>
                <TableCell align="center" sx={tableHeaderStyle}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {matrices.map((matrix, index) => (
                <TableRow
                  key={matrix._id}
                  sx={{
                    '&:hover': { bgcolor: '#1A2232' },
                  }}
                >
                  <TableCell sx={tableCellStyle}>
                    <strong>{index + 1}</strong>
                  </TableCell>
                  <TableCell sx={tableCellStyle}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {matrix.productDependencies.map((dep, i) => (
                        <Chip
                          key={i}
                          label={dep}
                          size="small"
                          variant="outlined"
                          sx={{
                            bgcolor: 'rgba(25, 118, 210, 0.2)',
                            color: '#90caf9',
                            borderColor: '#1976d2',
                            fontSize: '0.75rem',
                          }}
                        />
                      ))}
                    </Box>
                  </TableCell>
                  <TableCell sx={tableCellStyle}>
                    <Typography component="pre" sx={{ my: 0, color: '#e0e0e0', fontFamily: 'monospace', fontSize: '0.875rem' }}>
                      [{matrix.widthArray.join(', ')}]
                    </Typography>
                  </TableCell>
                  <TableCell sx={tableCellStyle}>
                    <Typography component="pre" sx={{ my: 0, color: '#e0e0e0', fontFamily: 'monospace', fontSize: '0.875rem' }}>
                      [{matrix.heightArray.join(', ')}]
                    </Typography>
                  </TableCell>
                  <TableCell align="center" sx={tableCellStyle}>
                    <IconButton
                      component={Link}
                      to={`/matrix/view-matrix/${matrix._id}`}
                      sx={{
                        color: '#90caf9',
                        '&:hover': {
                          bgcolor: 'rgba(25, 118, 210, 0.08)',
                          color: '#64b5f6',
                        },
                      }}
                      title="View Matrix Details"
                    >
                      <Eye size={18} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          sx={{
            width: '100%',
            bgcolor: snackbarSeverity === 'success' ? '#1b5e20' : snackbarSeverity === 'warning' ? '#e65100' : '#c62828',
            color: '#ffffff',
          }}
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MatrixListing;